import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authApi } from '../services/api';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import AuthHeader from '../components/AuthHeader';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('pending'); // pending, loading, success, error
  const [message, setMessage] = useState('');
  
  const token = searchParams.get('token');
  const tokenId = searchParams.get('id');  // Token ID - REQUIRED

  const handleVerify = async () => {
    // BOTH id and token are REQUIRED
    if (!tokenId || !token) {
      setStatus('error');
      setMessage('Invalid verification link');
      return;
    }

    setStatus('loading');

    try {
      const { data } = await authApi.verifyEmail(token, tokenId);
      setStatus('success');
      setMessage(data.message);
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.detail || 'Verification failed');
    }
  };

  // If missing id or token, show error immediately
  if (!tokenId || !token) {
    return (
      <div className="min-h-screen auth-bg flex items-center justify-center p-4" style={{ backgroundImage: 'url(/cockpit/hero.jpg)' }}>
        <div className="absolute inset-0 bg-black/80" />
        
        <div className="relative w-full max-w-md" data-testid="verify-email-page">
          <div className="card-panel p-8 text-center">
            <AuthHeader />
            <div className="inline-flex items-center justify-center w-16 h-16 bg-status-error/20 rounded-full mb-4">
              <XCircle className="w-8 h-8 text-status-error" />
            </div>
            <h1 className="text-2xl font-semibold text-cockpit-text-primary mb-2">Invalid Link</h1>
            <p className="text-cockpit-text-secondary mb-6">This verification link is invalid or missing.</p>
            <Link to="/login" className="btn-secondary inline-block">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center p-4" style={{ backgroundImage: 'url(/cockpit/hero.jpg)' }}>
      <div className="absolute inset-0 bg-black/80" />
      
      <div className="relative w-full max-w-md" data-testid="verify-email-page">
        <div className="card-panel p-8 text-center">
          <AuthHeader />
          {/* Pending state - waiting for user to click verify */}
          {status === 'pending' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-airbus-blue/20 rounded-full mb-4">
                <Mail className="w-8 h-8 text-airbus-blue" />
              </div>
              <h1 className="text-2xl font-semibold text-cockpit-text-primary mb-2">Verify Your Email</h1>
              <p className="text-cockpit-text-secondary mb-6">
                Click the button below to verify your email address and activate your account.
              </p>
              <button 
                onClick={handleVerify}
                className="btn-primary inline-block w-full py-3"
                data-testid="verify-email-btn"
              >
                Verify Email Address
              </button>
            </>
          )}

          {/* Loading state */}
          {status === 'loading' && (
            <>
              <Loader2 className="w-12 h-12 text-airbus-blue animate-spin mx-auto mb-4" />
              <h1 className="text-2xl font-semibold text-cockpit-text-primary mb-2">Verifying Email</h1>
              <p className="text-cockpit-text-secondary">Please wait...</p>
            </>
          )}

          {/* Success state */}
          {status === 'success' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-status-success/20 rounded-full mb-4">
                <CheckCircle className="w-8 h-8 text-status-success" />
              </div>
              <h1 className="text-2xl font-semibold text-cockpit-text-primary mb-2">Email Verified!</h1>
              <p className="text-cockpit-text-secondary mb-6" data-testid="verify-success-message">{message}</p>
              <Link to="/login" className="btn-primary inline-block w-full py-3" data-testid="login-after-verify">
                Sign In
              </Link>
            </>
          )}

          {/* Error state */}
          {status === 'error' && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-status-error/20 rounded-full mb-4">
                <XCircle className="w-8 h-8 text-status-error" />
              </div>
              <h1 className="text-2xl font-semibold text-cockpit-text-primary mb-2">Verification Failed</h1>
              <p className="text-cockpit-text-secondary mb-6" data-testid="verify-error-message">{message}</p>
              <div className="space-y-3">
                <button 
                  onClick={handleVerify}
                  className="btn-primary inline-block w-full py-3"
                >
                  Try Again
                </button>
                <Link to="/login" className="btn-secondary inline-block w-full py-3">
                  Back to Login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
